// React imports
import React, { useEffect, useState, useCallback } from "react";

// External library imports
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  Divider,
  Paper,
  Typography,
  TextField,
  Alert,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import FeedbackIcon from "@mui/icons-material/Feedback";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import SaveIcon from "@mui/icons-material/Save";
import Grid from "@mui/material/Grid";
import PersonIcon from "@mui/icons-material/Person";
import BadgeIcon from "@mui/icons-material/Badge";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import { useDispatch } from "react-redux";
import { ThunkDispatch } from "@reduxjs/toolkit";

// Local imports
import { RootState } from "../../../store/Store";
import {
  fetchData,
  postData,
  FetchDataSuccessAction,
  FetchDataFailureAction,
} from "../../../store/actions";
import { useCustomToast } from "../../../components/Methods/SnackBar";
import { employeeKRAsUrl, submit360FeedbackUrl, check360AvailabilityUrl, get360FeedbackUrl } from "../../../Service/Apis";
import {
  getStatusChip,
  formatDate,
  KRAStatus,
  getPeriodShortLabel,
  getPeriodDaysWarningColor,
} from "../utils";
import RatingSlider from "../../../components/RatingSlider/RatingSlider";
import { EmployeeKRA, ManagerFeedbackData, AvailabilityInfo, ManagerAvailabilityInfo } from "./Types/Feedback360Types";

// Rating categories configuration
const RATING_CATEGORIES = [
  {
    key: 'communication_rating' as const,
    title: 'Communication & Transparency',
    description: 'How well does your manager communicate goals, expectations, and feedback?'
  },
  {
    key: 'support_rating' as const,
    title: 'Support & Guidance',
    description: 'Does your manager provide adequate support when you face challenges?'
  },
  {
    key: 'recognition_rating' as const,
    title: 'Recognition & Appreciation',
    description: 'How well does your manager recognize and appreciate your contributions?'
  },
  {
    key: 'development_rating' as const,
    title: 'Career Development & Growth',
    description: 'Does your manager actively support your career growth and skill development?'
  },
  {
    key: 'leadership_rating' as const,
    title: 'Leadership & Decision Making',
    description: 'How effectively does your manager lead the team and make decisions?'
  }
] as const;

const Feedback360: React.FC = () => {
  // useState hooks
  const [myKRAs, setMyKRAs] = useState<EmployeeKRA[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [feedbackDialogOpen, setFeedbackDialogOpen] = useState<boolean>(false);
  const [selectedKRA, setSelectedKRA] = useState<EmployeeKRA | null>(null);
  const [selectedManagerInfo, setSelectedManagerInfo] = useState<ManagerAvailabilityInfo | null>(null);
  const [submittingFeedback, setSubmittingFeedback] = useState<boolean>(false);
  const [loadingFeedback, setLoadingFeedback] = useState<{ [key: string]: boolean }>({});
  // Per-KRA availability map keyed by employee_kra_id
  const [availabilityMap, setAvailabilityMap] = useState<Record<string, AvailabilityInfo>>({});
  // Legacy single availability (kept for dialog logic compatibility)
  const [availabilityInfo, setAvailabilityInfo] = useState<AvailabilityInfo | null>(null);
  
  const [managerFeedback, setManagerFeedback] = useState<ManagerFeedbackData>({
    communication_rating: 0,
    support_rating: 0,
    recognition_rating: 0,
    development_rating: 0,
    leadership_rating: 0,
    comments: "",
  });

  // Other hooks
  const dispatch: ThunkDispatch<RootState, null, FetchDataSuccessAction | FetchDataFailureAction> = useDispatch();
  const showToast = useCustomToast();

  // Utility Functions
  const handleError = useCallback((error: unknown, userMessage: string, shouldToast: boolean = true): void => {
    if (shouldToast) {
      showToast(userMessage, "error", true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // showToast is stable, no need to re-create handleError

  // Functions
  const fetchMyKRAs = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const response = await dispatch(fetchData(employeeKRAsUrl));

      if (response && Array.isArray(response)) {
        const eligibleKRAs = (response as (EmployeeKRA & { has_360_enabled?: boolean })[]).filter(
          (kra) => kra.has_360_enabled === true
        );
        setMyKRAs(eligibleKRAs);

        // Fetch availability independently per KRA
        const availMap: Record<string, AvailabilityInfo> = {};
        await Promise.all(
          eligibleKRAs.map(async (kra) => {
            try {
              const avail = await dispatch(
                fetchData(`${check360AvailabilityUrl}?kra_id=${kra.employee_kra_id}`)
              );
              if (avail) {
                availMap[kra.employee_kra_id] = avail as AvailabilityInfo;
              }
            } catch {
              // Silently ignore per-KRA availability errors
            }
          })
        );
        setAvailabilityMap(availMap);

        // Keep legacy single-info for dialog compat (use first available or first KRA)
        const firstEntry = Object.values(availMap)[0] ?? null;
        setAvailabilityInfo(firstEntry);
      } else {
        setMyKRAs([]);
      }
    } catch (error) {
      handleError(error, "Failed to load feedback opportunities");
      setMyKRAs([]);
    } finally {
      setLoading(false);
    }
  }, [dispatch, handleError]);

  const getDraftKey = (): string | null => {
    // Use the selected KRA + manager (per-manager multi-manager model) for the draft key
    if (selectedKRA?.employee_kra_id && selectedManagerInfo?.manager_id) {
      return `360_feedback_draft_${selectedKRA.employee_kra_id}_${selectedManagerInfo.manager_id}`;
    }
    // Fallback to legacy single-manager/calendar-year info if present
    if (availabilityInfo?.manager?.email && availabilityInfo?.calendar_year) {
      return `360_feedback_draft_${availabilityInfo.manager.email}_${availabilityInfo.calendar_year}`;
    }
    return null;
  };

  const loadDraftFromLocalStorage = (): boolean => {
    try {
      const draftKey = getDraftKey();
      if (!draftKey) return false;
      
      const savedDraft = localStorage.getItem(draftKey);
      
      if (savedDraft) {
        const draft = JSON.parse(savedDraft);
        setManagerFeedback(draft);
        showToast("Draft loaded from previous session", "info", true);
        return true;
      }
    } catch (error) {
      // Silently fail - draft loading is not critical
    }
    return false;
  };

  const saveDraftToLocalStorage = (): void => {
    try {
      const draftKey = getDraftKey();
      if (!draftKey) {
        showToast("Cannot save draft - manager info not available", "error", true);
        return;
      }
      
      localStorage.setItem(draftKey, JSON.stringify(managerFeedback));
      showToast("Draft saved successfully! You can continue later.", "success", true);
    } catch (error) {
      showToast("Failed to save draft", "error", true);
    }
  };

  const clearDraftFromLocalStorage = (): void => {
    try {
      const draftKey = getDraftKey();
      if (!draftKey) return;
      
      localStorage.removeItem(draftKey);
    } catch (error) {
      // Silently fail
    }
  };

  const loadExistingFeedback = useCallback(async (kraId: string): Promise<void> => {
    try {
      const response = await dispatch(fetchData(get360FeedbackUrl(kraId)));
      if (response && response.has_feedback && response.data) {
        setManagerFeedback({
          communication_rating: response.data.communication_rating || 0,
          support_rating: response.data.support_rating || 0,
          recognition_rating: response.data.recognition_rating || 0,
          development_rating: response.data.development_rating || 0,
          leadership_rating: response.data.leadership_rating || 0,
          comments: response.data.comments || "",
        });
      }
    } catch (error) {
      handleError(error, "Failed to load existing feedback");
    }
  }, [dispatch, handleError]);

  const handleOpenFeedbackDialog = async (kra: EmployeeKRA, managerInfo?: ManagerAvailabilityInfo): Promise<void> => {
    const dialogKey = managerInfo ? `${kra.employee_kra_id}_${managerInfo.manager_id}` : kra.employee_kra_id;
    setLoadingFeedback(prev => ({ ...prev, [dialogKey]: true }));

    setSelectedKRA(kra);
    setSelectedManagerInfo(managerInfo || null);
    // Sync legacy single availability info with the selected KRA's availability
    setAvailabilityInfo(availabilityMap[kra.employee_kra_id] ?? null);

    try {
      const isAlreadySubmitted = managerInfo ? !managerInfo.available : false;
      if (isAlreadySubmitted) {
        await loadExistingFeedback(kra.employee_kra_id);
      } else {
        const draftLoaded = loadDraftFromLocalStorage();
        if (!draftLoaded) {
          setManagerFeedback({
            communication_rating: 0,
            support_rating: 0,
            recognition_rating: 0,
            development_rating: 0,
            leadership_rating: 0,
            comments: "",
          });
        }
      }

      setFeedbackDialogOpen(true);
    } finally {
      setLoadingFeedback(prev => ({ ...prev, [dialogKey]: false }));
    }
  };

  const isViewMode = selectedManagerInfo ? !selectedManagerInfo.available : false;

  const handleSubmitFeedback = async (): Promise<void> => {
    // Validation
    const overallRating = calculateOverallRating();
    if (overallRating === 0) {
      showToast("Please provide at least one rating for your manager", "error", true);
      return;
    }

    if (isViewMode) {
      showToast("360° feedback is not available at this time", "error", true);
      return;
    }

    setSubmittingFeedback(true);
    
    try {
      // Submit feedback via API (new calendar year based endpoint)
      const payload: {
        communication_rating: number;
        support_rating: number;
        recognition_rating: number;
        development_rating: number;
        leadership_rating: number;
        comments: string;
        employee_kra_reference_id?: string;
        manager_id?: string;
      } = {
        communication_rating: managerFeedback.communication_rating,
        support_rating: managerFeedback.support_rating,
        recognition_rating: managerFeedback.recognition_rating,
        development_rating: managerFeedback.development_rating,
        leadership_rating: managerFeedback.leadership_rating,
        comments: managerFeedback.comments,
        manager_id: selectedManagerInfo?.manager_id || availabilityInfo?.manager?.manager_id,
      };
      
      // Optionally include KRA reference if available
      if (selectedKRA) {
        payload.employee_kra_reference_id = selectedKRA.employee_kra_id;
      }
      
      await dispatch(postData(submit360FeedbackUrl, payload));

      const kraId = selectedKRA?.employee_kra_id;
      const mgrId = selectedManagerInfo?.manager_id;
      const periodLabel = availabilityInfo?.kra_period
        ? `${availabilityInfo.kra_period.start} → ${availabilityInfo.kra_period.end}`
        : String(availabilityInfo?.calendar_year ?? "this period");

      showToast(`360° feedback submitted successfully for ${periodLabel}!`, "success", true);

      clearDraftFromLocalStorage();

      // Only mark the specific KRA + manager as submitted in the availability map
      if (kraId) {
        setAvailabilityMap((prev) => {
          const kraAvail = prev[kraId];
          if (!kraAvail) return prev;
          return {
            ...prev,
            [kraId]: {
              ...kraAvail,
              available: kraAvail.managers
                ? kraAvail.managers.some(
                    (m) => m.manager_id !== mgrId && m.available
                  )
                : false,
              managers: kraAvail.managers?.map((m) =>
                m.manager_id === mgrId
                  ? { ...m, available: false, submitted_date: new Date().toISOString() }
                  : m
              ),
            },
          };
        });
      }

      setFeedbackDialogOpen(false);
      setSelectedKRA(null);
      
    } catch (error: unknown) {
      const axiosError = error as { payload?: { error?: string } } | Error;
      const errorMessage = ('payload' in axiosError && axiosError.payload?.error) || 
        (axiosError instanceof Error ? axiosError.message : undefined) || 
        "Failed to submit feedback";
      handleError(error, errorMessage);
    } finally {
      setSubmittingFeedback(false);
    }
  };

  const calculateOverallRating = (): number => {
    const ratings = [
      managerFeedback.communication_rating,
      managerFeedback.support_rating,
      managerFeedback.recognition_rating,
      managerFeedback.development_rating,
      managerFeedback.leadership_rating,
    ];
    
    const validRatings = ratings.filter(r => r > 0);
    if (validRatings.length === 0) return 0;
    
    // Calculate average of all 5 categories (only counting non-zero ratings)
    return validRatings.reduce((sum, r) => sum + r, 0) / validRatings.length;
  };

  // useEffect hooks
  useEffect(() => {
    fetchMyKRAs();
  }, [fetchMyKRAs]);

  if (loading) {
    return (
      <Box
        sx={{
          width: "100%",
          height: "80vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 10, mb: 5 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          sx={{ fontWeight: "bold", color: "primary.main", mb: 1 }}
        >
          360° Manager Feedback
        </Typography>
        <Typography variant="body1" color="black">
          Share your anonymous feedback to help your manager grow and strengthen the team
        </Typography>
      </Box>

      {/* Empty State or KRA Cards */}
      {myKRAs.length === 0 ? (
        <Paper elevation={2} sx={{ p: 6, textAlign: "center" }}>
          <FeedbackIcon
            sx={{ fontSize: 80, color: "black", mb: 2 }}
          />
          <Typography variant="h6" sx={{ mb: 1, fontWeight: "600" }}>
            No Feedback Opportunities Available
          </Typography>
          <Typography variant="body2" color="black">
            You don't have any KRA assignments with 360° feedback enabled. Contact HR if you believe this is an error.
          </Typography>
        </Paper>
      ) : (
        /* KRA Cards - Grid layout for side-by-side display */
        <Grid container spacing={3}>
          {myKRAs.map((kra) => {
            const kraAvail = availabilityMap[kra.employee_kra_id];
            const kraManagers = kraAvail?.managers ?? [];
            const allSubmitted = kraManagers.length > 0 && kraManagers.every((m) => !m.available);
            const kraPeriod = kraAvail?.kra_period;

            return (
            <Grid item xs={12} md={myKRAs.length > 1 ? 6 : 12} key={kra.employee_kra_id}>
              <Card
                elevation={3}
                sx={{
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  borderTop: "4px solid",
                  borderColor: "primary.main",
                }}
              >
                <CardContent sx={{ p: 3, flex: 1, display: "flex", flexDirection: "column" }}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", mb: 2 }}>
                    <Typography variant="h6" sx={{ fontWeight: "700" }}>
                      {kra.form_name}
                    </Typography>
                    {allSubmitted && (
                      <Chip
                        icon={<CheckCircleIcon />}
                        label="Submitted"
                        size="small"
                        color="success"
                      />
                    )}
                  </Box>

                  <Divider sx={{ mb: 2 }} />

                  <Stack spacing={1.5} sx={{ flex: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <BadgeIcon fontSize="small" color="primary" />
                      <Typography variant="body2">
                        <strong>Designation:</strong> {kra.designation}
                      </Typography>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <AccessTimeIcon fontSize="small" color="primary" />
                      <Typography variant="body2">
                        <strong>Review Period:</strong>{" "}
                        {getPeriodShortLabel(kra.review_period)}
                      </Typography>
                    </Box>

                    {/* Show KRA-tied feedback period */}
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                      <CalendarTodayIcon fontSize="small" color="primary" />
                      <Typography variant="body2">
                        <strong>Feedback Period:</strong>{" "}
                        {kraPeriod?.start
                          ? `${new Date(kraPeriod.start).toLocaleDateString("en-GB")} → ${
                              kraPeriod.end
                                ? new Date(kraPeriod.end).toLocaleDateString("en-GB")
                                : "Ongoing"
                            }`
                          : kra.period_start_date
                          ? `${new Date(kra.period_start_date).toLocaleDateString("en-GB")} → ${
                              kra.period_end_date
                                ? new Date(kra.period_end_date).toLocaleDateString("en-GB")
                                : "Ongoing"
                            }`
                          : "—"}
                      </Typography>
                    </Box>
                  </Stack>

                  <Divider sx={{ my: 2 }} />

                  {/* Per-manager feedback buttons */}
                  {kraManagers.length > 0 ? (
                    <Stack spacing={1.5}>
                      {kraManagers.map((mgrInfo) => {
                        const btnKey = `${kra.employee_kra_id}_${mgrInfo.manager_id}`;
                        return (
                          <Box key={mgrInfo.manager_id} sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <Box>
                              <Typography variant="body2" fontWeight="bold">
                                {mgrInfo.level}: {mgrInfo.name}
                              </Typography>
                              {/* Show all role-project pairs stacked when the same
                                  manager holds both M1 and M2 under different projects;
                                  fall back to the single project field otherwise. */}
                              {mgrInfo.all_projects && mgrInfo.all_projects.length > 0 ? (
                                mgrInfo.all_projects.map((p, i) => (
                                  <Typography key={i} variant="caption" color="black" display="block">
                                    Project ({p.level}): {p.project}
                                  </Typography>
                                ))
                              ) : mgrInfo.project ? (
                                <Typography variant="caption" color="black">
                                  Project: {mgrInfo.project}
                                </Typography>
                              ) : null}
                              {mgrInfo.submitted_date && (
                                <Typography variant="caption" color="black" display="block">
                                  Submitted: {new Date(mgrInfo.submitted_date).toLocaleDateString("en-GB")}
                                </Typography>
                              )}
                            </Box>
                            <Button
                              variant={mgrInfo.available ? "contained" : "outlined"}
                              size="small"
                              onClick={() => handleOpenFeedbackDialog(kra, mgrInfo)}
                              disabled={loadingFeedback[btnKey] || !mgrInfo.available}
                              startIcon={loadingFeedback[btnKey] ? <CircularProgress size={16} /> : null}
                            >
                              {loadingFeedback[btnKey]
                                ? "Loading..."
                                : !mgrInfo.available
                                  ? "Submitted"
                                  : "Give Feedback"}
                            </Button>
                          </Box>
                        );
                      })}
                    </Stack>
                  ) : (
                    <Typography variant="body2" color="black">
                      No managers assigned for this KRA.
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Grid>
            );
          })}
        </Grid>
      )}

      {/* Feedback Dialog */}
      <Dialog
        open={feedbackDialogOpen}
        onClose={() => !submittingFeedback && setFeedbackDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        aria-labelledby="feedback-dialog-title"
        aria-describedby="feedback-dialog-description"
      >
        <DialogTitle id="feedback-dialog-title">
          <Box>
            <Typography variant="h6" sx={{ fontWeight: "bold" }}>
              {isViewMode ? "View 360° Feedback" : "Provide 360° Feedback"}
            </Typography>
            <Typography variant="body2" color="black" id="feedback-dialog-description">
              {selectedKRA?.form_name} •{" "}
              {selectedManagerInfo?.name || availabilityInfo?.manager?.name || selectedKRA?.manager_reviewed_by_name || "Your Manager"}
              {selectedManagerInfo && ` (${selectedManagerInfo.level})`}
              {availabilityInfo?.kra_period?.start && (
                ` • Period: ${new Date(availabilityInfo.kra_period.start).toLocaleDateString("en-GB")} → ${availabilityInfo.kra_period.end ? new Date(availabilityInfo.kra_period.end).toLocaleDateString("en-GB") : "Ongoing"}`
              )}
            </Typography>
            {isViewMode && (
              <Chip
                label="Already Submitted for this KRA period"
                size="small"
                color="success"
                sx={{ mt: 1 }}
              />
            )}
          </Box>
        </DialogTitle>
        
        <DialogContent dividers>
          {isViewMode && (
            <Alert severity="info" sx={{ mb: 3 }}>
              You have already submitted 360° feedback for this KRA period.
              {" "}A new submission will be available when your next KRA is assigned.
            </Alert>
          )}

          <Stack spacing={3}>
            {/* Rating Sliders - Using Reusable Component */}
            {RATING_CATEGORIES.map((category) => (
              <RatingSlider
                key={category.key}
                title={category.title}
                description={category.description}
                value={managerFeedback[category.key]}
                onChange={(value) => setManagerFeedback({
                  ...managerFeedback,
                  [category.key]: value
                })}
                disabled={isViewMode}
              />
            ))}

            <Divider />

            {/* Overall Comments */}
            <TextField
              label="Overall Comments (Optional)"
              multiline
              rows={4}
              fullWidth
              value={managerFeedback.comments}
              onChange={(e) => setManagerFeedback({
                ...managerFeedback,
                comments: e.target.value
              })}
              placeholder="Share your thoughts about your manager's strengths, areas for improvement, or any other feedback..."
              helperText={`${managerFeedback.comments.length}/2000 characters - Your feedback helps improve team dynamics and organizational culture`}
              disabled={isViewMode}
              inputProps={{ 
                maxLength: 2000,
                'aria-label': 'Overall feedback comments',
                'aria-describedby': 'comments-helper-text'
              }}
              id="comments-helper-text"
            />

            <Divider />

            {/* Overall Rating - Auto-Calculated */}
            <Box sx={{ bgcolor: "#f5f5f5", p: 2, borderRadius: 2 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: "600", mb: 1 }}>
                Overall Rating (Auto-Calculated)
              </Typography>
              <Box>
                <Typography variant="h4" sx={{ fontWeight: "700", color: "info.main", mb: 0.5 }}>
                  {calculateOverallRating().toFixed(2)}/5.00
                </Typography>
                <Typography variant="h6" sx={{ color: "info.main", fontWeight: "600" }}>
                  {((calculateOverallRating() / 5) * 100).toFixed(1)}%
                </Typography>
                <Typography variant="caption" color="black" sx={{ display: "block", mt: 1 }}>
                  Average of all category ratings
                </Typography>
              </Box>
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          {isViewMode ? (
            <Button onClick={() => setFeedbackDialogOpen(false)}>
              Close
            </Button>
          ) : (
            <>
              <Button 
                onClick={() => setFeedbackDialogOpen(false)}
                disabled={submittingFeedback}
              >
                Cancel
              </Button>
              <Button
                variant="outlined"
                onClick={saveDraftToLocalStorage}
                disabled={submittingFeedback || isViewMode}
                startIcon={<SaveIcon />}
                aria-label="Save 360 feedback draft to continue later"
              >
                Save Draft
              </Button>
              <Button
                variant="contained"
                onClick={handleSubmitFeedback}
                disabled={submittingFeedback || calculateOverallRating() === 0 || isViewMode}
                startIcon={submittingFeedback ? <CircularProgress size={20} /> : null}
                aria-busy={submittingFeedback}
                aria-label="Submit 360 degree feedback for your manager"
                aria-disabled={submittingFeedback || calculateOverallRating() === 0 || isViewMode}
              >
                {submittingFeedback ? "Submitting..." : "Submit Feedback"}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Feedback360;
